import React, { useEffect, useState } from 'react'

import { fetchApi } from '@hozana/api/fetchApi'
import { useApiData } from '@hozana/api/hooks/useApiData'
import { useAttributes } from '@hozana/dom/hooks/useAttributes'
import { usePrevious } from '@hozana/hooks/usePrevious'
import { useDispatch, useSelector } from '@hozana/redux/hooks'
import { QA } from '@hozana/tests/constants'
import { GTM } from '@hozana/tracking/gtm'
import { shallowDiffers } from '@hozana/utils/functions/objects'

import { Button } from 'elements/button/Button'
import { Div } from 'elements/layout/Div'
import { Space } from 'elements/layout/Space'
import { Icon } from 'elements/media/Icon'
import { Text } from 'elements/text/Text'

import { ICON } from 'config/icons'

import { useIntentionsWidget } from 'modules/intention/hooks/useIntentionsWidget'
import { selectIntentions, selectWidgetIntention } from 'modules/intention/selectors'
import { useUser } from 'modules/user/hooks/useUser'

import { getIntentionsQuery, sendIntentionsViewsMutation } from '../queries'
import { IntentionItem, TIntentionItemProps } from './IntentionItem'
import { IntentionSectionTitle } from './IntentionSectionTitle'
import { PreloadIntentionItem } from './PreloadIntentionItem'

export type TIntentionsListProps = Pick<TIntentionItemProps, 'trackingSource' | 'withWrapper'> & {
  landingPageSlug?: string
  refetchButton?: boolean
  /** Avoid sending views mutation when IntentionPanel is hidden */
  hidden?: boolean
}

export const IntentionsList: TFC<TIntentionsListProps> = ({
  withWrapper,
  landingPageSlug,
  refetchButton = true,
  hidden,
  trackingSource,
}) => {
  const [level, setLevel] = useState(0)
  const [exceptIntentionIds, setExceptIntentionIds] = useState<number[]>([])
  const dispatch = useDispatch()
  const { intention: myIntentionId } = useUser()
  const attrs = useAttributes() // used for accessibility to pass "tabindex = -1" to avoid focus

  const widget = useIntentionsWidget()
  const widgetIntention = useSelector(selectWidgetIntention)

  const [intentionIds, intentionsQuery] = useApiData(
    () => ({
      query: getIntentionsQuery({
        widgetId: widget?.id,
        landingPageSlug,
        level,
        exceptIntentionsIds: exceptIntentionIds,
      }),
    }),
    [landingPageSlug, widget?.id, level, exceptIntentionIds],
  )
  const prevIntentionIds = usePrevious(intentionIds)
  const intentions = useSelector((state) => selectIntentions(state, intentionIds))

  useEffect(() => {
    if (!hidden && intentionIds?.length > 0) {
      if (shallowDiffers(intentionIds, prevIntentionIds)) {
        dispatch(fetchApi(sendIntentionsViewsMutation({ intentionIds })))
      }
    }
  }, [dispatch, hidden, intentionIds, prevIntentionIds])

  const refetchIntentions = () => {
    const displayedIntentionsIds = widgetIntention ? [widgetIntention.id] : []
    intentions?.forEach((intention) => intention && displayedIntentionsIds.push(intention.id))
    setExceptIntentionIds(displayedIntentionsIds)

    GTM.trackEvent(GTM.EVENTS.INTENTIONS_SHUFFLE)
    // Don't increment higher than 3 as 4 is the max level allowed
    if (level < 3) {
      setLevel(level + 1)
    }
  }

  if (!intentions && !intentionsQuery.loading) {
    return null
  }

  const items = intentions
    // After beeing reported as an abuse, the intention is undefined: a check if exists is needed
    // Since intention page is loaded statically with intentions, the user's intention can be part of intentions
    ?.filter((intention) => intention?.id && intention.id !== myIntentionId)
    .slice(0, 3)

  return (
    <>
      <IntentionSectionTitle>trans:intention:word.intentions</IntentionSectionTitle>
      {intentionsQuery.loading &&
        [...Array(4)]
          .map((_, i) => i)
          .map((i) => <PreloadIntentionItem widgetId={widget?.id} withWrapper={withWrapper} key={i} />)}
      {items.map((intention) => (
        <Div key={intention.id} align="left" data-testid={QA.INTENTION.LIST_INTENTION_ITEM}>
          <IntentionItem {...{ intention, trackingSource, withWrapper }} />
          <Space h="16px" />
        </Div>
      ))}
      {intentions?.length > 0 && refetchButton && (
        <Div align="center">
          <Button type="button" colors="greyTransparent" onClick={refetchIntentions} bold={false} m="10px" {...attrs}>
            <Text m="0 0 10px">trans:intention:intention.shuffle</Text>
            <br />
            <Icon name={ICON.LOADER} size="1.6em" />
          </Button>
        </Div>
      )}
    </>
  )
}
